import { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "./supabase";
import { apiGet, apiPost } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null); // { role, status, company_name, full_name }
  const [loading, setLoading] = useState(true);

  // Fetch profile from backend
  const fetchProfile = async (accessToken) => {
    try {
      const data = await apiGet("/auth/me", accessToken);
      if (data.profile) {
        setProfile(data.profile);
      } else {
        setProfile(null);
      }
    } catch (err) {
      console.error("Failed to fetch profile:", err);
      // Profile might not exist yet (new signup before register-recruiter call)
      setProfile(null);
    }
  };

  // Refresh profile (called after approval polling or after registration)
  const refreshProfile = async () => {
    if (session?.access_token) {
      await fetchProfile(session.access_token);
    }
  };

  useEffect(() => {
    // Demo OTP auto-login: verify OTP first, then check session
    const params = new URLSearchParams(window.location.search);
    const demoOtp = params.get("demo_otp");
    const demoEmail = params.get("demo_email");

    const initSession = async () => {
      if (demoOtp && demoEmail) {
        params.delete("demo_otp");
        params.delete("demo_email");
        const clean = params.toString();
        window.history.replaceState({}, "", clean ? `?${clean}` : window.location.pathname);

        const { error } = await supabase.auth.verifyOtp({ email: demoEmail, token: demoOtp, type: "magiclink" });
        if (error) console.error("Demo OTP verify failed:", error);
      }

      const { data: { session: s } } = await supabase.auth.getSession();
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.access_token) {
        await fetchProfile(s.access_token);
      }
      setLoading(false);
    };

    initSession();

    // Listen for auth changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, s) => {
        setSession(s);
        setUser(s?.user ?? null);
        if (s?.access_token) {
          await fetchProfile(s.access_token);
        } else {
          setProfile(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signUp = async (email, password) => {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
    return data;
  };

  const registerRecruiter = async (fullName, companyName, accessToken) => {
    const data = await apiPost(
      "/auth/register-recruiter",
      { full_name: fullName, company_name: companyName },
      accessToken
    );
    if (data.profile) setProfile(data.profile);
    return data;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
  };

  const resetPassword = async (email) => {
    const redirectTo = `${window.location.origin}${window.location.pathname}`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) throw error;
  };

  // Passwordless email sign-in for existing recruiters.
  // shouldCreateUser:false routes new emails through the sign-up flow
  // (which captures full_name + company_name and gates on careers-admin approval).
  const sendMagicLink = async (email) => {
    const emailRedirectTo = `${window.location.origin}${window.location.pathname}`;
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo, shouldCreateUser: false },
    });
    if (error) throw error;
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      loading,
      signIn,
      signUp,
      signOut,
      registerRecruiter,
      refreshProfile,
      resetPassword,
      sendMagicLink,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
